import React, { useRef, useEffect, useState } from "react";
import { McModal, McButton } from "@maersk-global/mds-react-wrapper/components-core";

export default function InvoiceNumberModal({ open, onClose, chargeId, parentPONo, invoiceNumber, onSuccess }) {
  const modalRef = useRef(null);
  const invoiceInputRef = useRef(null);
  const chargeIdRef = useRef(null); // Use ref to immediately capture chargeId
  const [currentChargeId, setCurrentChargeId] = useState(chargeId); // Initialize with prop
  const [loading, setLoading] = useState(false); // Add loading state for API calls

  // Immediately capture chargeId in ref whenever it changes
  useEffect(() => {
    if (chargeId) {
      chargeIdRef.current = chargeId;
      if (chargeId !== currentChargeId) {
        setCurrentChargeId(chargeId);
      }
    }
  }, [chargeId, currentChargeId]);

  // Debug props when component receives them and store chargeId in state
  useEffect(() => {
    if (open && chargeId) {
      setCurrentChargeId(chargeId);
    }
  }, [chargeId, invoiceNumber, open]);

  // Reset modal state when opened
  useEffect(() => {
    if (!modalRef.current) return;
    try { 
      modalRef.current.open = open; 
    } catch { 
      modalRef.current.setAttribute("open", ""); 
    }

    if (open) {
      if (invoiceInputRef.current) invoiceInputRef.current.value = "";
      setLoading(false); // Reset loading state when modal opens
    }
  }, [open, chargeId, invoiceNumber]);

  // Handle submitting Invoice Number
  const handleConfirm = async () => {
    // Use multiple fallbacks: state, ref, prop
    const activeChargeId = currentChargeId || chargeIdRef.current || chargeId;

    const invoiceNumber = invoiceInputRef.current.value.trim();
    if (!invoiceNumber) {
      alert("Please enter a valid Invoice Number (numbers only)");
      return;
    }

    if (!activeChargeId) {
      console.error("Cannot submit: both chargeId prop and currentChargeId state are null");
      alert("Error: Charge ID is missing. Please close the modal and try again.");
      return;
    }

    setLoading(true); // Start loading

    const payload = {
      chargeNoticeId: activeChargeId,
      invoiceNumber
    };

    try {
      const token = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/update-invoice`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` ,"consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY},
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error("Network response was not ok");
      const data = await res.json();
      alert("Invoice Number processed successfully!");
      onSuccess?.(activeChargeId, data.updatedInvoiceNumber);
      onClose();
    } catch (err) {
      alert("Failed to update Invoice Number. Please try again.");
    } finally {
      setLoading(false); // Stop loading
    }
  };

  return (
    <McModal ref={modalRef} heading="Invoice Number">
      <div style={{ padding: "1rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
        <h3>Please provide the Invoice Number</h3>

        {loading && (
          <div style={{ textAlign: "center", padding: "1rem" }}>
            <div className="loader" style={{ margin: "0 auto" }}></div>
            <p>Processing invoice number...</p>
          </div>
        )}

        {!loading && (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <label htmlFor="invoiceNumber">Enter Invoice Number</label>
            <input
              ref={invoiceInputRef}
              id="invoiceNumber"
              type="text"
              placeholder="Enter Invoice Number"
              style={{ padding: "0.5rem", borderRadius: "4px", border: "1px solid #ccc" }}
            />
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
              <McButton appearance="primary" onClick={handleConfirm}>Submit</McButton>
              <McButton appearance="secondary" onClick={onClose}>Cancel</McButton>
            </div>
          </div>
        )}
      </div>
    </McModal>
  );
}
